import React, { useCallback, useState } from "react"

import { WithLoadingIndicator } from "~tailwindui/Basics"

import airbrake from "src/airbrake-notifier"

import { gql, useClient, useMutation, useQuery } from "hooks/urql"

import AdminRoom from "../Admin/AudioRooms/AdminRoom"
import TeacherJoinModal from "../Pages/Teacher/AudioRooms/TeacherJoinModal"
import TeacherRoom from "../Pages/Teacher/AudioRooms/TeacherRoom"
import StudentRoom from "../Student/AudioRooms/StudentRoom"

import JoinModal from "./JoinModal"
import { ChimeJoinInfo, HandleAudioRoomError } from "./types"
import useAudioInputDevices from "./useAudioInputDevices"

type AudioRoomUserType = "Teacher" | "Student" | "Admin"

export type AudioRoomProps = {
  audioRoomId: string
  currentUserId: string
  userType: AudioRoomUserType
}

type JoinState = {
  joinInfo: ChimeJoinInfo
  controlsUnlockedUserIds: string[]
}

const AudioRoom: React.FC<AudioRoomProps> = ({
  audioRoomId,
  currentUserId,
  userType,
}) => {
  const client = useClient()
  const [joinAudioRoomDetailsResult] = useQuery({
    query: joinAudioRoomDetailsQuery,
    client,
    variables: { audioRoomId },
  })
  const [mutationState, runMutation]: any[] = useMutation(
    joinAudioRoomMutation,
    client
  )
  const [joinState, setJoinState] = useState<JoinState | null>(null)
  const [userError, setUserError] = useState<string>()
  const {
    audioInputDevices,
    selectedAudioInputDeviceId,
    setSelectedAudioInputDeviceId,
    selectedAudioInputDeviceName,
    microphoneError,
  } = useAudioInputDevices()

  const handleError: HandleAudioRoomError = useCallback(
    ({ airbrakeMessage, error }) => {
      if (airbrakeMessage)
        airbrake.notify({
          message: airbrakeMessage,
          error,
          params: {
            name: "AudioRoom/index.tsx",
            roomId: audioRoomId,
            userId: currentUserId,
          },
        })
    },
    [audioRoomId, currentUserId]
  )

  const handleJoin = () => {
    runMutation({ audioRoomId })
      .then(result => {
        const { failures, joinInfo, audioRoom } = result.data.joinAudioRoom

        if (failures.length > 0) {
          setUserError(
            "Sorry, unable to join classroom. Please refresh to try again"
          )
          handleError({
            airbrakeMessage: `User unable to join audio room: ${failures[0].message}`,
          })
        } else {
          setJoinState({
            joinInfo,
            controlsUnlockedUserIds: audioRoom?.controlsUnlockedUserIds ?? [],
          })
        }
      })
      .catch(e => {
        setUserError(
          "Sorry, unable to join classroom. Please refresh to try again"
        )
        handleError({
          airbrakeMessage: "User unable to join audio room",
          error: e,
        })
      })
  }

  return (
    <div className="min-h-screen overflow-hidden bg-[#111] text-white">
      {userError ? (
        <p className="p-6 text-center italic text-white/60">{userError}</p>
      ) : !joinState?.joinInfo ? (
        <WithLoadingIndicator result={joinAudioRoomDetailsResult}>
          {({ data: { audioRoom } }) => {
            const joinModalProps = {
              activeAcrClassPeriod: audioRoom?.activeAcrClassPeriod ?? null,
              audioInputDevices,
              selectedAudioInputDeviceId,
              setSelectedAudioInputDeviceId,
              microphoneError,
              onJoin: handleJoin,
              joining: mutationState.fetching,
            }

            return userType === "Teacher" ? (
              <TeacherJoinModal {...joinModalProps} />
            ) : (
              <JoinModal
                {...joinModalProps}
                hideMicSelect={userType === "Student"}
              />
            )
          }}
        </WithLoadingIndicator>
      ) : userType === "Teacher" ? (
        <TeacherRoom
          joinInfo={joinState.joinInfo}
          handleError={handleError}
          setUserError={setUserError}
          audioRoomId={audioRoomId}
          currentUserId={currentUserId}
          audioInputDeviceId={selectedAudioInputDeviceId}
          audioInputDeviceName={selectedAudioInputDeviceName}
          controlsUnlockedUserIds={joinState.controlsUnlockedUserIds}
        />
      ) : userType === "Admin" ? (
        <AdminRoom
          joinInfo={joinState.joinInfo}
          handleError={handleError}
          setUserError={setUserError}
          audioRoomId={audioRoomId}
          currentUserId={currentUserId}
          audioInputDeviceId={selectedAudioInputDeviceId}
          audioInputDeviceName={selectedAudioInputDeviceName}
          controlsUnlockedUserIds={joinState.controlsUnlockedUserIds}
        />
      ) : (
        <StudentRoom
          joinInfo={joinState.joinInfo}
          handleError={handleError}
          setUserError={setUserError}
          audioRoomId={audioRoomId}
          currentUserId={currentUserId}
          audioInputDeviceId={selectedAudioInputDeviceId}
          audioInputDeviceName={selectedAudioInputDeviceName}
        />
      )}
    </div>
  )
}

const joinAudioRoomDetailsQuery = gql`
  query AudioRoomJoinDetails($audioRoomId: ID!) {
    audioRoom(id: $audioRoomId) {
      activeAcrClassPeriod {
        startsAt
        endsAt
        acrClass {
          name
          subject {
            name
          }
          teacher {
            fullName
          }
        }
      }
    }
  }
`

const joinAudioRoomMutation = gql`
  mutation JoinAudioRoom($audioRoomId: ID!) {
    joinAudioRoom(audioRoomId: $audioRoomId) {
      joinInfo {
        attendee {
          attendeeId
          externalUserId
          joinToken
        }
        meeting
      }
      audioRoom {
        controlsUnlockedUserIds
      }
      failures {
        message
      }
    }
  }
`

export default AudioRoom
